/**
 * FpsEmoteWheel.ts
 * Phase 13 — 12-slot radial emote wheel (hold B → release to trigger)
 */

// ─── Types ────────────────────────────────────────────────────────────────────

export type EmoteId =
  | 'dance_floss'
  | 'dance_dab'
  | 'dance_worm'
  | 'wave'
  | 'sit'
  | 'applaud'
  | 'point'
  | 'thumbsup'
  | 'taunt'
  | 'laugh'
  | 'salute'
  | 'victory_spin';

export interface EmoteTriggerResult {
  emoteId: EmoteId;
  /** Seconds the emote plays for */
  duration: number;
}

// ─── Emote catalogue ─────────────────────────────────────────────────────────

interface EmoteSlot {
  id: EmoteId;
  label: string;
  icon: string;
  duration: number; // seconds
}

export type BotEmoteKind = 'victory' | 'taunt';

const BOT_EMOTE_IDS: Record<BotEmoteKind, EmoteId> = {
  victory: 'victory_spin',
  taunt: 'taunt',
};

export function getBotEmoteDuration(kind: BotEmoteKind): number {
  const id = BOT_EMOTE_IDS[kind];
  const slot = EMOTES.find((emote) => emote.id === id);
  return slot?.duration ?? 2;
}

export function getBotEmoteLabel(kind: BotEmoteKind): string {
  const id = BOT_EMOTE_IDS[kind];
  const slot = EMOTES.find((emote) => emote.id === id);
  return slot ? `${slot.icon} ${slot.label}` : kind;
}

const EMOTES: EmoteSlot[] = [
  { id: 'dance_floss',   label: 'Floss',        icon: '💃', duration: 4.0 },
  { id: 'dance_dab',     label: 'Dab',          icon: '🕺', duration: 1.5 },
  { id: 'dance_worm',    label: 'The Worm',     icon: '🐛', duration: 3.0 },
  { id: 'wave',          label: 'Wave',         icon: '👋', duration: 2.0 },
  { id: 'sit',           label: 'Sit Down',     icon: '🪑', duration: 5.0 },
  { id: 'applaud',       label: 'Applause',     icon: '👏', duration: 2.5 },
  { id: 'point',         label: 'Point',        icon: '👆', duration: 1.5 },
  { id: 'thumbsup',      label: 'Thumbs Up',    icon: '👍', duration: 1.5 },
  { id: 'taunt',         label: 'Taunt',        icon: '😜', duration: 2.0 },
  { id: 'laugh',         label: 'Laugh',        icon: '😂', duration: 2.5 },
  { id: 'salute',        label: 'Salute',       icon: '🫡', duration: 1.5 },
  { id: 'victory_spin',  label: 'Victory Spin', icon: '🌀', duration: 3.5 },
];

// ─── FpsEmoteWheel ────────────────────────────────────────────────────────────

export class FpsEmoteWheel {
  // DOM root
  private readonly root: HTMLElement;
  // Individual slot elements (index matches EMOTES)
  private readonly slots: HTMLElement[] = [];
  // Center label
  private readonly centerEl: HTMLElement;

  // State
  private _open = false;
  private _selectedIndex = -1; // -1 = center / none
  private _mouseX = 0;
  private _mouseY = 0;

  // Animation
  private _opacity = 0;
  private _scale = 0.85;

  // Wheel geometry
  private static readonly RADIUS = 140; // px from center to slot midpoint
  private static readonly SLOT_SIZE = 56;  // px

  constructor(private readonly mount: HTMLElement) {
    this.root = this._buildRoot();
    this.centerEl = this._buildCenter();
    this._buildSlots();
    this._applyClosedState(true /* instant */);
    mount.appendChild(this.root);
  }

  // ── Public API ──────────────────────────────────────────────────────────────

  onKeyDown(code: string): void {
    if (code === 'KeyB' && !this._open) {
      this._open = true;
      this._selectedIndex = -1;
    }
  }

  onKeyUp(code: string): EmoteTriggerResult | null {
    if (code !== 'KeyB') return null;
    const picked = this._selectedIndex;
    this._open = false;
    this._selectedIndex = -1;
    this._updateSlotHighlights();
    if (picked < 0 || picked >= EMOTES.length) return null;
    const emote = EMOTES[picked]!;
    this._showToast(emote);
    return { emoteId: emote.id, duration: emote.duration };
  }

  onMouseMove(x: number, y: number): void {
    this._mouseX = x;
    this._mouseY = y;
    if (this._open) {
      this._selectedIndex = this._pickSlotFromMouse();
      this._updateSlotHighlights();
    }
  }

  isOpen(): boolean {
    return this._open;
  }

  /**
   * Call every frame with delta-time in seconds.
   * Animates the overlay opacity and scale.
   */
  update(dt: number): void {
    const targetOpacity = this._open ? 1 : 0;
    const targetScale   = this._open ? 1 : 0.85;
    const speed = 1 / 0.12; // 120 ms transition

    this._opacity += (targetOpacity - this._opacity) * Math.min(1, dt * speed);
    this._scale   += (targetScale   - this._scale)   * Math.min(1, dt * speed);

    const visible = this._opacity > 0.01;
    this.root.style.display   = visible ? 'flex' : 'none';
    this.root.style.opacity   = String(this._opacity.toFixed(3));
    this.root.style.transform = `translate(-50%, -50%) scale(${this._scale.toFixed(4)})`;

    if (this._open) {
      this._selectedIndex = this._pickSlotFromMouse();
      this._updateSlotHighlights();
    }
  }

  dispose(): void {
    this.root.remove();
  }

  // ── Private helpers ─────────────────────────────────────────────────────────

  private _buildRoot(): HTMLElement {
    const el = document.createElement('div');
    el.setAttribute('aria-label', 'Emote Wheel');
    el.setAttribute('role', 'dialog');
    Object.assign(el.style, {
      position:        'fixed',
      top:             '50%',
      left:            '50%',
      transform:       'translate(-50%, -50%) scale(0.85)',
      width:           '360px',
      height:          '360px',
      borderRadius:    '50%',
      background:      'radial-gradient(circle, rgba(0,0,0,0.82) 60%, rgba(0,0,0,0.0) 100%)',
      zIndex:          '9000',
      pointerEvents:   'auto',
      display:         'none',
      opacity:         '0',
      alignItems:      'center',
      justifyContent:  'center',
      userSelect:      'none',
    } as Partial<CSSStyleDeclaration>);
    return el;
  }

  private _buildCenter(): HTMLElement {
    const el = document.createElement('div');
    el.textContent = 'EMOTE';
    Object.assign(el.style, {
      position:   'absolute',
      top:        '50%',
      left:       '50%',
      transform:  'translate(-50%, -50%)',
      color:      '#ffffff',
      fontFamily: 'system-ui, sans-serif',
      fontSize:   '13px',
      fontWeight: '700',
      letterSpacing: '2px',
      textTransform: 'uppercase',
      pointerEvents: 'none',
    } as Partial<CSSStyleDeclaration>);
    this.root.appendChild(el);
    return el;
  }

  private _buildSlots(): void {
    const count = EMOTES.length; // 12
    for (let i = 0; i < count; i++) {
      const emote = EMOTES[i]!;
      const angle = (i / count) * Math.PI * 2 - Math.PI / 2; // start at top
      const x = Math.cos(angle) * FpsEmoteWheel.RADIUS;
      const y = Math.sin(angle) * FpsEmoteWheel.RADIUS;

      const slot = document.createElement('div');
      slot.dataset['index'] = String(i);

      Object.assign(slot.style, {
        position:        'absolute',
        width:           `${FpsEmoteWheel.SLOT_SIZE}px`,
        height:          `${FpsEmoteWheel.SLOT_SIZE}px`,
        top:             `calc(50% + ${y}px - ${FpsEmoteWheel.SLOT_SIZE / 2}px)`,
        left:            `calc(50% + ${x}px - ${FpsEmoteWheel.SLOT_SIZE / 2}px)`,
        borderRadius:    '12px',
        background:      'rgba(15, 15, 20, 0.78)',
        border:          '2px solid rgba(255,255,255,0.18)',
        display:         'flex',
        flexDirection:   'column',
        alignItems:      'center',
        justifyContent:  'center',
        gap:             '2px',
        cursor:          'pointer',
        transition:      'transform 80ms ease, border-color 80ms ease, background 80ms ease',
        transform:       'scale(1)',
        backdropFilter:  'blur(6px)',
      } as Partial<CSSStyleDeclaration>);

      const iconEl = document.createElement('span');
      iconEl.textContent = emote.icon;
      iconEl.style.fontSize = '22px';
      iconEl.style.lineHeight = '1';

      const labelEl = document.createElement('span');
      labelEl.textContent = emote.label;
      Object.assign(labelEl.style, {
        color:      '#e0e0e0',
        fontSize:   '9px',
        fontFamily: 'system-ui, sans-serif',
        fontWeight: '600',
        textAlign:  'center',
        maxWidth:   '52px',
        overflow:   'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        pointerEvents: 'none',
      } as Partial<CSSStyleDeclaration>);

      slot.appendChild(iconEl);
      slot.appendChild(labelEl);
      this.root.appendChild(slot);
      this.slots.push(slot);
    }
  }

  private _applyClosedState(instant = false): void {
    this.root.style.display   = 'none';
    this.root.style.opacity   = '0';
    this.root.style.transform = 'translate(-50%, -50%) scale(0.85)';
    if (instant) {
      this._opacity = 0;
      this._scale   = 0.85;
    }
  }

  /**
   * Returns the slot index (0-11) that the mouse angle points to,
   * or -1 if the mouse is within the dead-zone radius (center area).
   */
  private _pickSlotFromMouse(): number {
    const rect = this.root.getBoundingClientRect();
    const cx = rect.left + rect.width  / 2;
    const cy = rect.top  + rect.height / 2;
    const dx = this._mouseX - cx;
    const dy = this._mouseY - cy;
    const dist = Math.sqrt(dx * dx + dy * dy);

    // Dead-zone: treat as "center / no selection" when close to middle
    if (dist < 36) return -1;

    // Angle in [0, 2π), 0 = top
    let angle = Math.atan2(dy, dx) + Math.PI / 2;
    if (angle < 0) angle += Math.PI * 2;

    const count = EMOTES.length;
    return Math.floor(angle / (Math.PI * 2 / count)) % count;
  }

  private _updateSlotHighlights(): void {
    for (let i = 0; i < this.slots.length; i++) {
      const slot = this.slots[i]!;
      const active = i === this._selectedIndex;
      slot.style.transform    = active ? 'scale(1.1)'  : 'scale(1)';
      slot.style.borderColor  = active ? '#ffd700'     : 'rgba(255,255,255,0.18)';
      slot.style.background   = active
        ? 'rgba(40, 30, 0, 0.92)'
        : 'rgba(15, 15, 20, 0.78)';
    }
  }

  private _showToast(emote: EmoteSlot): void {
    const toast = document.createElement('div');
    toast.textContent = `${emote.icon}  ${emote.label}`;
    Object.assign(toast.style, {
      position:    'fixed',
      top:         '50%',
      left:        '50%',
      transform:   'translate(-50%, -50%)',
      background:  'rgba(0,0,0,0.72)',
      color:       '#ffffff',
      fontFamily:  'system-ui, sans-serif',
      fontSize:    '22px',
      fontWeight:  '700',
      padding:     '16px 32px',
      borderRadius:'12px',
      zIndex:      '9100',
      pointerEvents: 'none',
      whiteSpace:  'nowrap',
    } as Partial<CSSStyleDeclaration>);
    document.body.appendChild(toast);
    setTimeout(() => toast.remove(), 2000);
  }
}
